import { LoginPage, LogoutPage, NoAccess, SettingsPage } from '@moveup/app-core/views';

const AuthAdminDashboard = () => import('@/views/AuthAdminDashboard.vue');

const UserEdit = () => import('@/views/UserEdit.vue');
const UsersList = () => import('@/views/UsersList.vue');

const ApplicationEdit = () => import('@/views/ApplicationEdit.vue');
const ApplicationNew = () => import('@/views/ApplicationNew.vue');
const ApplicationsList = () => import('@/views/ApplicationsList.vue');

const PlatformFlavors = () => import('@/views/PlatformFlavors.vue');
const PlatformFlavorEdit = () => import('@/views/PlatformFlavorEdit.vue')

import { checkAuth } from '@moveup/app-core/auth';
import store from '@/store';
import CodeList from '@/views/codes/CodeList.vue';
import CodeEdit from '@/views/codes/CodeEdit.vue';
import TemplateList from '@/views/TemplateList.vue';
import TemplateEdit from '@/views/TemplateEdit.vue';

export function checkCreateUserPermission(to, from, next) {
  const selectedContext = store.state.app?.selectedContext;

  // Contexts not loaded yet — allow navigation; component enforces once loaded
  if (!selectedContext) return next();

  // Root context — always unrestricted
  if (selectedContext.name === 'moveup' && !selectedContext.parentContextId) {
    return next();
  }

  const globalPerm = selectedContext.contentPermissions?.find(
    p => p.contentType === 'user' && !p.rootId
  );

  // No contentPermissions entry for 'user' — allow all (backwards compat)
  if (!globalPerm) return next();

  const canCreate = globalPerm.permissions.includes('create');
  if (!canCreate) return next('/users');
  return next();
}

const routes = [
	{
		path: '/',
		redirect: '/login',
	},
	{
		path: '/login',
		name: 'Login',
		component: LoginPage,
	},
	{
		path: '/logout',
		name: 'Logout',
		component: LogoutPage,
	},
	{
		path: '/no-access',
		name: 'NoAccess',
		component: NoAccess,
	},
	{
		path: '/dashboard',
		name: 'AuthAdminDashboard',
		component: AuthAdminDashboard,
		beforeEnter: checkAuth,
	},
	{
		path: '/settings',
		name: 'Settings',
		component: SettingsPage,
		beforeEnter: checkAuth(['user:write', 'user:write-self']),
	},
	{
		path: '/users',
		name: 'UsersList',
		component: UsersList,
		beforeEnter: checkAuth(['user:read']),
	},
	{
		path: '/users/page/:page',
		name: 'UsersPage',
		component: UsersList,
		beforeEnter: checkAuth(['user:read']),
	},
	{
		path: '/user/:id',
		name: 'UserEdit',
		component: UserEdit,
		beforeEnter: checkAuth(['user:write']),
	},
	{
		path: '/user',
		name: 'UserEdit',
		component: UserEdit,
		beforeEnter: (to, from, next) => {
			checkAuth(['user:write'])(to, from, (arg) => {
				if (arg) return next(arg);  // scope check failed — let checkAuth redirect
				checkCreateUserPermission(to, from, next);
			});
		},
	},
	{
		path: '/applications',
		name: 'ApplicationsList',
		component: ApplicationsList,
		beforeEnter: checkAuth(['application:read']),
	},
	{
		path: '/applications/page/:page',
		name: 'ApplicationsPage',
		component: ApplicationsList,
		beforeEnter: checkAuth(['application:read']),
	},
	{
		path: '/application/:id',
		name: 'ApplicationEdit',
		component: ApplicationEdit,
		beforeEnter: checkAuth(['application:write']),
	},
	{
		path: '/platformflavors',
		name: 'PlatformFlavors',
		component: PlatformFlavors,
		beforeEnter: checkAuth,
	},
	{
		path: '/platformflavor/:id',
		name: 'PlatformFlavorEdit',
		component: PlatformFlavorEdit,
		// Read access is enough to view; saving is gated on application:write in the view.
		beforeEnter: checkAuth(['application:read']),
	},
	{
		path: '/application',
		name: 'ApplicationNew',
		component: ApplicationNew,
		beforeEnter: checkAuth(['application:write']),
	},
	{
		path: '/codes',
		name: 'CodeList',
		component: CodeList,
		beforeEnter: checkAuth(['code:read']),
	},
	{
		path: '/codes/page/:page',
		name: 'CodeList',
		component: CodeList,
		beforeEnter: checkAuth(['code:read']),
	},
	{
		path: '/code/:id',
		name: 'CodeEdit',
		component: CodeEdit,
		beforeEnter: checkAuth(['code:write']),
	},
	{
		path: '/code',
		name: 'CodeEdit',
		component: CodeEdit,
		beforeEnter: checkAuth(['code:write']),
	},
	{
		path: '/templates',
		name: 'TemplateList',
		component: TemplateList,
		beforeEnter: checkAuth(['user:read']),
	},
	{
		path: '/template/:id',
		name: 'TemplateEdit',
		component: TemplateEdit,
		beforeEnter: checkAuth(['user:write']),
	},
	{
		path: '/template',
		name: 'TemplateEdit',
		component: TemplateEdit,
		beforeEnter: checkAuth(['user:write']),
	}
];

export default routes;
